import { CardBody, useRect } from '@statsbomb/kitbag-components';
import {
  BOX_PLUS_CHANNELS_GRID_LINES,
  BOX_PLUS_CHANNELS_GRID_ZONES,
  GradientBar,
  GridColoursLayer,
  GridLinesLayer,
  Legend,
  PitchViz,
  useGradient,
} from '@statsbomb/kitbag-datavis';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { showLegendAtom, showMarkerStartAtom } from '@/atoms/visualisation';
import { useGetPitchRotation } from '@/hooks/useGetPitchRotation';
import { useLoadableStatus } from '@/hooks/useLoadableStatus';
import { usePitchCrop } from '@/hooks/usePitchCrop';
import { useStatus } from '@/hooks/useStatus';
import { EventBucketsProps } from '@/types/visualisation';
import { getZoneEventCounts, ZoneEventPoint } from '@/utils/eventBuckets';
import { isNullish } from '@/utils/predicates';
import { CardHeightWrapper } from '../card/CardHeightWrapper';
import { ErrorCard } from '../contentState/ErrorCard';
import { EventMapSelectionLayer } from './EventMap/EventMapSelectionLayer';
import { PitchVisWithAttackingDirectionArrow } from './PitchVisualisation/PitchVisWithAttackingDirectionArrow';
import { PitchVisWrapper } from './PitchVisualisation/PitchVisWrapper';

const NO_EVENTS: ZoneEventPoint[] = [];

const EventBucketsBase = ({ events = NO_EVENTS, overlay }: { events?: ZoneEventPoint[]; overlay?: JSX.Element }) => {
  const pitchRotation = useGetPitchRotation(false);
  const visibleArea = usePitchCrop(false);
  const showLegend = useAtomValue(showLegendAtom);
  const { t } = useTranslation('visualisation');
  const [legendRef, legendRect] = useRect<HTMLLegendElement>();
  const { gradientScale } = useGradient('divergent');

  const zones = useMemo(() => {
    const ratioByZoneId = new Map(getZoneEventCounts(events).map(({ id, ratio }) => [id, ratio]));
    return BOX_PLUS_CHANNELS_GRID_ZONES.map(zone => ({
      ...zone,
      fill: gradientScale(ratioByZoneId.get(zone.id) ?? 0),
    }));
  }, [events, gradientScale]);

  const pitchViz = (
    <PitchViz
      rotationName={pitchRotation}
      pitchFocusZoneName={visibleArea}
      overlay={overlay}
      underlay={
        <>
          <GridColoursLayer zones={zones} />
          <GridLinesLayer lines={BOX_PLUS_CHANNELS_GRID_LINES} />
        </>
      }
    />
  );

  return (
    <PitchVisWrapper>
      {showLegend ? (
        <>
          <PitchVisWithAttackingDirectionArrow pitchVisType="heatmap" pitchRotation={pitchRotation}>
            {pitchViz}
          </PitchVisWithAttackingDirectionArrow>
          <Legend legendRef={legendRef}>
            <GradientBar
              colSpan={2}
              gradientName="divergent"
              title={t('legend.eventBuckets.title')}
              minText={t('legend.eventBuckets.minText')}
              maxText={t('legend.eventBuckets.maxText')}
              legendWidth={legendRect.width}
            />
          </Legend>
        </>
      ) : (
        pitchViz
      )}
    </PitchVisWrapper>
  );
};

const EventBucketsWithData = ({ pitchEventsAtom, unwrappedPitchEventsAtom, allowInteraction }: EventBucketsProps) => {
  const { markerEvents, arrowEvents } = useAtomValue(unwrappedPitchEventsAtom);
  const showMarkerStart = useAtomValue(showMarkerStartAtom);
  const status = useLoadableStatus(pitchEventsAtom);
  const statusProps = useStatus('general', status);

  const events = useMemo(
    () =>
      [...markerEvents, ...arrowEvents].map(({ startX, startY, endX, endY }) => ({
        startX: showMarkerStart || isNullish(endX) ? startX : endX,
        startY: showMarkerStart || isNullish(endY) ? startY : endY,
      })),
    [markerEvents, arrowEvents, showMarkerStart],
  );

  const selectionOverlay = allowInteraction ? (
    <EventMapSelectionLayer unwrappedPitchEventsAtom={unwrappedPitchEventsAtom} />
  ) : undefined;

  return (
    <CardHeightWrapper>
      <CardBody {...statusProps}>
        <EventBucketsBase events={events} overlay={selectionOverlay} />
      </CardBody>
    </CardHeightWrapper>
  );
};

export const EventBuckets = (props: EventBucketsProps) => (
  <ErrorCard translationNameSpace="general" fallbackChildren={<EventBucketsBase />}>
    <EventBucketsWithData {...props} />
  </ErrorCard>
);
